import { useEffect, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Loader2, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState } from '@/components/EmptyState';
import { ListPagination } from '@/components/resources/ListPagination';
import { ListToolbar, type ResourceFilterDef } from '@/components/resources/ListToolbar';
import { hasActiveFilters, pageCount, type ListQuery } from '@/lib/listQuery';
import { cn } from '@/lib/utils';

export interface ResourceColumn<T> {
  key: string;
  header: string;
  /** Sortable columns sort by their `key` (it must be in the page's `sortableColumns`). */
  sortable?: boolean;
  cell: (row: T) => ReactNode;
  /** Native tooltip for the cell (use for truncated content). */
  title?: (row: T) => string | undefined;
  className?: string;
  hideBelow?: 'md' | 'lg';
}

export type { ResourceFilterDef };

export interface ResourceRowAction<T> {
  id: string;
  label: (row: T) => string;
  icon: LucideIcon;
  destructive?: boolean;
  enabled: (row: T) => boolean;
  /** Short reason appended to the tooltip while the action is disabled. */
  disabledReason?: (row: T) => string | undefined;
  busy?: (row: T) => boolean;
  run: (row: T) => void;
}

export interface ResourceListProps<T> {
  columns: ResourceColumn<T>[];
  visibleColumns: string[];
  onToggleColumn: (key: string) => void;
  rows: T[];
  total: number;
  getRowKey: (row: T) => string | number;
  query: ListQuery;
  /** Only used when `searchLabel` is set. */
  onSearch?: (s: string) => void;
  onFilter: (key: string, value: string | undefined) => void;
  onSort: (col: string) => void;
  /** MUST navigate (update the URL page); it is also called by the beyond-last-page clamp. Pass a stable callback. */
  onPage: (p: number) => void;
  onLimit: (n: number) => void;
  onClearFilters: () => void;
  filters?: ResourceFilterDef[];
  /** The search input is rendered only when this is provided. */
  searchLabel?: string;
  searchPlaceholder?: string;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  /** Used by the error states' Retry buttons. */
  onRetry: () => void;
  /** Toolbar refresh button; defaults to `onRetry`. */
  onRefresh?: () => void;
  /** Accessible name of the table. */
  tableLabel?: string;
  rowActions?: ResourceRowAction<T>[];
  toolbarExtra?: ReactNode;
  empty: { title: string; description?: string; action?: ReactNode };
}

const HIDE_BELOW = { md: 'hidden md:table-cell', lg: 'hidden lg:table-cell' } as const;
const SKELETON_ROWS = 6;

/** Presentational, URL-state-driven list: toolbar, sticky-header table, pagination and all load states. */
export function ResourceList<T>(props: ResourceListProps<T>) {
  const {
    columns, visibleColumns, rows, total, getRowKey, query, filters = [], rowActions = [],
    loading, refreshing, error, onRetry, onPage, onClearFilters, empty,
  } = props;
  const shown = columns.filter((c) => visibleColumns.includes(c.key));
  const active = hasActiveFilters(query);
  const pages = pageCount(total, query.limit);

  // Page clamp: after rows are removed (or filters shrink the result) the URL may point past the last
  // page. `onPage` is the parent's router navigation, not state owned by this component, so calling
  // it from an effect does not trigger a cascading local render.
  const beyondLastPage = !loading && !refreshing && !error && total > 0 && rows.length === 0 && query.page > pages;
  useEffect(() => {
    if (beyondLastPage) onPage(pages);
  }, [beyondLastPage, pages, onPage]);

  const retry = <Button type="button" variant="outline" size="sm" onClick={onRetry}>Retry</Button>;
  const clear = <Button type="button" variant="outline" size="sm" onClick={onClearFilters}>Clear filters</Button>;

  const toolbar = (
    <ListToolbar
      search={query.search} searchLabel={props.searchLabel} searchPlaceholder={props.searchPlaceholder} onSearch={props.onSearch}
      filters={filters} filterValues={query.filters} onFilter={props.onFilter}
      columns={columns} visibleColumns={visibleColumns} onToggleColumn={props.onToggleColumn}
      refreshing={refreshing} onRefresh={props.onRefresh ?? onRetry} extra={props.toolbarExtra}
      resultText={loading ? '' : `${total} ${total === 1 ? 'result' : 'results'}`}
      canClear={active} onClear={onClearFilters}
    />
  );

  const headerRow = (
    <TableHeader>
      <TableRow>
        {shown.map((c) => {
          const sorted = query.sortBy === c.key ? query.sortOrder : null;
          return (
            <TableHead
              key={c.key}
              className={cn('sticky top-0 z-10 bg-card shadow-[inset_0_-1px_0_var(--border)]', c.hideBelow && HIDE_BELOW[c.hideBelow], c.className)}
              aria-sort={sorted ? (sorted === 'asc' ? 'ascending' : 'descending') : undefined}
            >
              {c.sortable ? (
                <button
                  type="button" onClick={() => props.onSort(c.key)}
                  className="inline-flex items-center gap-1 rounded-sm outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {c.header}
                  {sorted && (sorted === 'asc' ? <ArrowUp className="size-3" aria-hidden="true" /> : <ArrowDown className="size-3" aria-hidden="true" />)}
                </button>
              ) : c.header}
            </TableHead>
          );
        })}
        {rowActions.length > 0 && (
          <TableHead className="sticky top-0 z-10 bg-card shadow-[inset_0_-1px_0_var(--border)]">
            <span className="sr-only">Actions</span>
          </TableHead>
        )}
      </TableRow>
    </TableHeader>
  );

  const tableShell = (tbody: ReactNode, dim: boolean) => (
    <div
      aria-busy={dim || undefined}
      className={cn('max-h-[70vh] overflow-auto rounded-lg border bg-card', dim && 'opacity-60 transition-opacity')}
    >
      <Table containerClassName="overflow-visible" aria-label={props.tableLabel}>
        {headerRow}
        <TableBody>{tbody}</TableBody>
      </Table>
    </div>
  );

  let body: ReactNode;
  if (loading) {
    body = tableShell(
      Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <TableRow key={i} aria-hidden="true" className="h-12 hover:bg-transparent">
          {shown.map((c) => (
            <TableCell key={c.key} className={cn(c.hideBelow && HIDE_BELOW[c.hideBelow])}><div className="h-3 animate-pulse rounded bg-muted" /></TableCell>
          ))}
          {rowActions.length > 0 && <TableCell><div className="h-3 animate-pulse rounded bg-muted" /></TableCell>}
        </TableRow>
      )),
      false,
    );
  } else if (error && rows.length === 0) {
    body = <div role="alert"><EmptyState title="Couldn't load data" description={error} action={retry} /></div>;
  } else if (rows.length === 0) {
    body = beyondLastPage ? null : active ? (
      <EmptyState title="No results match your filters" description="Try changing or clearing the search and filters." action={clear} />
    ) : (
      <EmptyState {...empty} />
    );
  } else {
    body = (
      <>
        {error && (
          <div role="alert" className="mb-3 flex items-center justify-between gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm">
            <span>Showing last known data — refresh failed.</span>
            {retry}
          </div>
        )}
        {tableShell(
          rows.map((row) => (
            <TableRow key={getRowKey(row)} className="h-12">
              {shown.map((c) => (
                <TableCell key={c.key} title={c.title?.(row)} className={cn(c.hideBelow && HIDE_BELOW[c.hideBelow], c.className)}>{c.cell(row)}</TableCell>
              ))}
              {rowActions.length > 0 && (
                <TableCell className="max-w-none">
                  <div className="flex items-center justify-end gap-0.5">
                    {rowActions.map((a) => {
                      const enabled = a.enabled(row);
                      const busy = a.busy?.(row) ?? false;
                      const text = a.label(row);
                      const reason = !enabled ? a.disabledReason?.(row) : undefined;
                      const Icon = a.icon;
                      return (
                        <Button
                          key={a.id} type="button" variant="ghost" size="icon-sm" aria-label={text}
                          title={reason ? `${text} (${reason})` : text}
                          aria-disabled={!enabled || busy || undefined}
                          aria-busy={busy || undefined}
                          className={cn('aria-disabled:hover:bg-transparent', a.destructive && 'text-destructive hover:text-destructive')}
                          onClick={() => { if (enabled && !busy) a.run(row); }}
                        >
                          {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Icon aria-hidden="true" />}
                        </Button>
                      );
                    })}
                  </div>
                </TableCell>
              )}
            </TableRow>
          )),
          refreshing,
        )}
      </>
    );
  }

  return (
    <div className="space-y-3 focus:outline-none" data-focus-fallback tabIndex={-1}>
      {toolbar}
      <p role="status" className="sr-only">{loading || beyondLastPage ? 'Loading…' : ''}</p>
      {body}
      <ListPagination page={query.page} limit={query.limit} total={total} onPage={onPage} onLimit={props.onLimit} />
    </div>
  );
}
